import useIsUserLoggedIn from 'config/hooks/useIsUserLoggedIn';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import i18next from 'i18next';
import useStripeObject from 'config/hooks/useStripeObject';
import useUserOrLocalTimeHour from 'config/hooks/useUserOrLocalTimeHour';
import { Elements } from '@stripe/react-stripe-js';
import { Box, Grid } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useLocalStorage } from '../../config/hooks/useLocalStorage';
import useEvent from '../../config/hooks/useEvent';
import { parseLocale } from '../../config/Localization';
import { Api, SECURE_LOGIN_BACK_URL } from '../../environment/environment';
import Attendees from './parts/Attendees';
import BuyButton from './parts/BuyButton';
import EventName from './parts/EventName';
import EventDateLarge from './parts/EventDateLarge';
import ModalBuyEvent from './parts/ModalBuyEvent';
import UserScore from './parts/UserScore';
import Loader from '../../components/Loader';
import ShareEvent from './parts/ShareEvent';
import PrivateEventInfo from './parts/PrivateEventInfo';
import ViewersCount from './parts/ViewersCount';
import PreviewBox from './parts/PreviewBox';
import ModalSuccess from './parts/ModalSuccess';
import { thunkGetCurrentUser } from '../../store/actions/CurrentUser.actions';
import EventSpecialActions from '../../components/events/EventSpecialActions/EventSpecialActions';
import { useStyles } from './styles';
import { useResponsiveSize } from '../../config/hooks/useResponsiveSize';
import { DeviceSensitive, UIBreakPoint } from '../StreamingWEBRTC/config';
import EventDetails from './parts/EventDetails/EventDetails';
import Footer from '../../parts/Footer';
import Header from '../../parts/Header';
import RallyLogin from './parts/RallyLogin';

const EventPage: FC = () => {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useDispatch();
    const location = useLocation();
    const isMobileUI = useResponsiveSize(UIBreakPoint, DeviceSensitive);

    const { id } = useParams<{ id: string }>();

    const [noSuccessTicket, , clearTicketSuccess] = useLocalStorage<{ message: string } | false>(
        'ticketSuccess',
        false,
    );

    const isUserLoggedIn = useIsUserLoggedIn();
    const {
        renderBuyButton,
        eventData,
        loading,
        live,
        purchased,
        eventType,
        hasAdvertisingEnabled,
        upcoming,
        isCreator,
    } = useEvent(id);

    const stripePromise = useStripeObject();
    const {
        currency,
        event_presentation_video_landscape,
        event_num_attendees,
        event_title,
        event_description,
        event_languages,
        event_tags,
        event_date,
        event_num_attendees_streaming,
        event_num_tickets_free_streaming,
        event_price_ticket_streaming,
        event_cover_image,
        event_streaming_start_date,
        event_uuid,
        event_price_ticket,
        event_thanks_video_landscape,
        event_num_tickets_free,
        event_promotional_code_discount,
        ticket,
        ticket_streaming,
    } = eventData.event;

    const { user_username } = eventData.user;
    const {
        influencer: { influencer_avatar, influencer_ranking },
    } = eventData;

    const [modalBuyEvent, setModalBuyEvent] = useState<boolean>(false);
    const [inAppBrowse, setInAppBrowse] = useState<boolean>(false);
    const [modalPurchaseSuccess, setModalPurchaseSuccess] = useState<boolean>(false);
    const [loadingPaymentConfirmation, setLoadingPaymentConfirmation] = useState<boolean>(false);
    const [, setConfirmPaypal] = useState<Record<string, unknown>>({});
    const [email, setEmail] = useState<string>('');
    const [confirmEmail, setConfirmEmail] = useState<string>('');

    const eventStart = useMemo(() => {
        return moment(parseInt(event_streaming_start_date || '', 10));
    }, [event_streaming_start_date]);

    const startIn = useMemo(() => {
        const now = moment().locale(parseLocale());
        return now.to(eventStart);
    }, [eventStart]);

    const { time } = useUserOrLocalTimeHour(event_date || '', eventData.event);

    const getCurrentUser = useCallback(() => {
        dispatch(thunkGetCurrentUser());
    }, [dispatch]);

    const getPaypalOrderStatus = useCallback(
        async (paypalPayment: { success: boolean; paypalId: string | undefined }) => {
            try {
                const status = await axios.post(`${Api}payment/paypal_order`, {
                    paypal_order_id: paypalPayment.paypalId,
                });

                if (
                    status.data &&
                    (status.data.data.status === 'COMPLETED' || status.data.data.status === 'APPROVED')
                ) {
                    setLoadingPaymentConfirmation(false);
                    setModalPurchaseSuccess(true);
                } else {
                    setLoadingPaymentConfirmation(false);
                    enqueueSnackbar(i18next.t('EventPageComponentErrorProcessingEventPayment'), {
                        variant: 'error',
                    });
                }
            } catch (err) {
                setLoadingPaymentConfirmation(false);
                enqueueSnackbar(i18next.t('EventPageComponentErrorProcessingEventPayment'), {
                    variant: 'error',
                });
            }
        },
        [enqueueSnackbar],
    );

    useEffect(() => {
        const browser = navigator.userAgent.toLowerCase();
        if (
            browser.includes('instagram') ||
            browser.includes('linkedin') ||
            browser.includes('discord') ||
            browser.includes('slack')
        ) {
            setInAppBrowse(true);
        }
    }, []);

    useEffect(() => {
        if (noSuccessTicket) {
            enqueueSnackbar(
                noSuccessTicket.message ? noSuccessTicket.message : 'This ticket has probably expired!',
                { variant: 'error' },
            );
            clearTicketSuccess();
        }
    }, [clearTicketSuccess, enqueueSnackbar, noSuccessTicket]);

    /* Effect to update user when purchase is successful */
    useEffect(() => {
        if (modalPurchaseSuccess) {
            getCurrentUser();
        }
    }, [getCurrentUser, modalPurchaseSuccess]);

    /* Effect checking if we're on a callback from paypal. if so, trigger getPaypalOrderStatus */
    useEffect(() => {
        let success = true;
        const params = location.search;
        let paypalId;
        const paypalIntent = localStorage.getItem('paypalIntent')
            ? JSON.parse(localStorage.getItem('paypalIntent') || '{}')
            : undefined;
        let storedToken = null;
        let email;
        if (paypalIntent) {
            storedToken = paypalIntent.token;
            if (paypalIntent.email) {
                email = paypalIntent.email;
                setEmail(email);
            }
        }
        if (params && params.includes('paypal_callback_error')) {
            success = false;
            paypalId = params.substring(params.indexOf('=') + 1);
        } else if (params.includes('paypal_callback')) {
            paypalId = params.substring(params.indexOf('=') + 1, params.indexOf('&Payer'));
        }

        if (paypalId === storedToken) {
            setLoadingPaymentConfirmation(true);
            setConfirmPaypal({ success, paypalId });
            getPaypalOrderStatus({ success, paypalId });
        }
        localStorage.removeItem('paypalIntent');
    }, [getPaypalOrderStatus, location]);

    const renderModals = () => (
        <>
            {modalPurchaseSuccess && (
                <ModalSuccess
                    email={email}
                    setModalPurchaseSuccess={setModalPurchaseSuccess}
                    isUserLoggedIn={isUserLoggedIn}
                    modalPurchaseSuccess={modalPurchaseSuccess}
                    event_thanks_video={event_thanks_video_landscape}
                />
            )}

            {modalBuyEvent && (
                <Elements stripe={stripePromise}>
                    <ModalBuyEvent
                        currency={currency}
                        confirmEmail={confirmEmail}
                        setConfirmEmail={setConfirmEmail}
                        email={email}
                        setEmail={setEmail}
                        mobileNav={isMobileUI}
                        modalDetail
                        setModalDetail={setModalBuyEvent}
                        onGetUser={getCurrentUser}
                        setModalPurchaseSuccess={setModalPurchaseSuccess}
                        eventData={eventData}
                    />
                </Elements>
            )}
        </>
    );

    if (loading || loadingPaymentConfirmation) {
        return (
            <Box className={classes.loader}>
                <Loader />
            </Box>
        );
    }

    const renderCommonUI = () => (
        <>
            {!upcoming && <ViewersCount eventData={eventData} />}

            <EventDetails description={event_description} languages={event_languages} tags={event_tags} />
        </>
    );

    const renderRightSideUI = () => (
        <Grid item lg={4} xl={3} xs={12} className={classes.rightSideColumn}>
            {!isMobileUI && renderBuyButton && (
                <>
                    <BuyButton
                        mobileNav={isMobileUI}
                        setModalBuyEvent={setModalBuyEvent}
                        inAppBrowse={inAppBrowse}
                        num_tickets_free={
                            event_num_tickets_free !== null
                                ? event_num_tickets_free
                                : event_num_tickets_free_streaming
                        }
                        event_price={
                            event_price_ticket !== null ? event_price_ticket : event_price_ticket_streaming
                        }
                        currency={currency}
                    />

                    <RallyLogin
                        eventId={event_uuid}
                        remainingSeats={
                            event_num_tickets_free !== null
                                ? event_num_tickets_free
                                : event_num_tickets_free_streaming
                        }
                    />
                </>
            )}

            <ShareEvent
                isCreator={isCreator}
                url={`${SECURE_LOGIN_BACK_URL}/event/${event_uuid}`}
                title={event_title}
            />
            <Attendees eventData={eventData} />
        </Grid>
    );

    const renderMobileUI = () => (
        <>
            <Grid item xs={12}>
                <PreviewBox
                    isMobile
                    eventDate={event_date}
                    event_presentation_video={event_presentation_video_landscape}
                    event_cover_image={event_cover_image}
                    upcoming={upcoming}
                    live={live}
                    purchased={purchased}
                    isCreator={isCreator}
                    eventType={eventType}
                    startIn={startIn}
                    event_uuid={event_uuid}
                />

                <Grid container className={classes.dateAndCategory}>
                    <Grid item xs={3}>
                        <EventDateLarge day={time.day} month={time.month} />
                    </Grid>

                    <Grid item xs={9}>
                        <EventName
                            upcoming={upcoming}
                            title={event_title}
                            hour={time.time}
                            timeZone={time.gmtOffset}
                        />
                    </Grid>
                </Grid>

                {renderBuyButton && (
                    <>
                        <BuyButton
                            mobileNav={isMobileUI}
                            setModalBuyEvent={setModalBuyEvent}
                            inAppBrowse={inAppBrowse}
                            num_tickets_free={
                                event_num_tickets_free !== null
                                    ? event_num_tickets_free
                                    : event_num_tickets_free_streaming
                            }
                            event_price={
                                event_price_ticket !== null
                                    ? event_price_ticket
                                    : event_price_ticket_streaming
                            }
                            currency={currency}
                            marginTop={-8}
                        />
                        <RallyLogin
                            eventId={event_uuid}
                            remainingSeats={
                                event_num_tickets_free !== null
                                    ? event_num_tickets_free
                                    : event_num_tickets_free_streaming
                            }
                        />
                    </>
                )}

                <UserScore
                    score={influencer_ranking}
                    userName={user_username}
                    userAvatar={influencer_avatar}
                />
            </Grid>

            <Grid item lg={6} xs={12} className={classes.middleColumn}>
                {renderCommonUI()}
            </Grid>

            <Grid item xs={12}>
                {isCreator && (
                    <PrivateEventInfo
                        isMobile={isMobileUI}
                        currency={currency}
                        upcoming={upcoming}
                        event_num_attendees_streaming={event_num_attendees_streaming}
                        event_num_attendees={event_num_attendees}
                        ticket_streaming={ticket_streaming}
                        ticket={ticket}
                        event_price_ticket={event_price_ticket}
                        event_price_ticket_streaming={event_price_ticket_streaming}
                        event_promotional_code_discount={event_promotional_code_discount}
                    />
                )}
            </Grid>
        </>
    );

    const renderDesktopUI = () => (
        <>
            <Grid item lg={1}>
                <EventDateLarge day={time.day} month={time.month} />
            </Grid>
            <Grid item lg={7} xl={8} className={classes.middleColumn}>
                <UserScore
                    score={influencer_ranking}
                    userName={user_username}
                    userAvatar={influencer_avatar}
                />
                <EventName
                    upcoming={upcoming}
                    title={event_title}
                    timeZone={time.gmtOffset}
                    hour={time.time}
                />
                <PreviewBox
                    eventDate={event_date}
                    event_presentation_video={event_presentation_video_landscape}
                    event_cover_image={event_cover_image}
                    upcoming={upcoming}
                    live={live}
                    purchased={purchased}
                    isCreator={isCreator}
                    eventType={eventType}
                    startIn={startIn}
                    event_uuid={event_uuid}
                />

                {renderCommonUI()}
            </Grid>
        </>
    );

    return (
        <Box className={classes.page}>
            <Header isCreator={isCreator} purchased={purchased} />
            <Box className={classes.pageContent}>
                <EventSpecialActions
                    hasAdvertisingEnabled={hasAdvertisingEnabled}
                    isCreator={isCreator}
                    uuid={event_uuid}
                    setLoading={setLoadingPaymentConfirmation}
                />

                <Grid container className={classes.container}>
                    {isMobileUI ? renderMobileUI() : renderDesktopUI()}
                    {renderRightSideUI()}
                </Grid>

                {isCreator && !isMobileUI && (
                    <PrivateEventInfo
                        isMobile={isMobileUI}
                        currency={currency}
                        upcoming={upcoming}
                        event_num_attendees_streaming={event_num_attendees_streaming}
                        event_num_attendees={event_num_attendees}
                        ticket_streaming={ticket_streaming}
                        ticket={ticket}
                        event_price_ticket={event_price_ticket}
                        event_price_ticket_streaming={event_price_ticket_streaming}
                        event_promotional_code_discount={event_promotional_code_discount}
                    />
                )}

                {renderModals()}
            </Box>
            <Footer />
        </Box>
    );
};

export default EventPage;
